import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSite } from "../context/SiteContext";
import { authFetch } from "../utils/authFetch";
import API_ENDPOINTS from "../apiConfig";
import Notification from "./Notification";
import ConfirmModal from "./ConfirmModal";
import UserManagement from "./UserManagement";
import SiteExport from "./SiteExport";
import { AREA_ICONS } from "../constants/areaIcons";


const CATEGORY_COLORS = [
  // 15 hues evenly spaced — medium (bright) variants
  '#EF4444', '#F97316', '#F59E0B', '#84CC16', '#22C55E',
  '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9', '#3B82F6',
  '#6366F1', '#8B5CF6', '#A855F7', '#D946EF', '#EC4899',
  // same 15 hues — dark variants
  '#B91C1C', '#C2410C', '#B45309', '#4D7C0F', '#15803D',
  '#047857', '#0F766E', '#0E7490', '#0369A1', '#1D4ED8',
  '#4338CA', '#6D28D9', '#7E22CE', '#A21CAF', '#BE185D',
  // neutrals
  '#111111',
];

const ColorSwatch = ({ color, selected, onClick }) => (
  <button
    title={color}
    onClick={onClick}
    style={{
      width: 22,
      height: 22,
      borderRadius: '50%',
      backgroundColor: color,
      border: 'none',
      padding: 0,
      cursor: 'pointer',
      boxShadow: selected
        ? '0 0 0 2px #fff, 0 0 0 4px #333'
        : '0 0 0 1px rgba(0,0,0,0.15)',
      flexShrink: 0,
    }}
  />
);

function SiteSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedSite, sites, setSites, loadingSites, areas, setAreas } = useSite();

  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') === 'users' ? 'users'
    : searchParams.get('tab') === 'categories' ? 'categories'
    : searchParams.get('tab') === 'special-plates' ? 'special-plates'
    : searchParams.get('tab') === 'export' ? 'export'
    : 'areas'
  );
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [newCatName, setNewCatName] = useState("");
  const [creating, setCreating] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [editingAreaId, setEditingAreaId] = useState(null);
  const [editAreaName, setEditAreaName] = useState("");
  const [editAreaSpaces, setEditAreaSpaces] = useState("");
  const [editAreaIcon, setEditAreaIcon] = useState("");

  const [areaGates, setAreaGates] = useState({});
  const [proximityEdits, setProximityEdits] = useState({});

  const [specialPlateRules, setSpecialPlateRules] = useState([]);
  const [loadingSpecialPlates, setLoadingSpecialPlates] = useState(false);

  useEffect(() => {
    if (!loadingSites && !selectedSite) navigate('/dashboard');
  }, [loadingSites, selectedSite, navigate]);

  useEffect(() => {
    if (localStorage.getItem("actualUserRole") === "gate_opener") navigate('/dashboard');
  }, []);

  useEffect(() => {
    if (activeTab !== 'special-plates' || !selectedSite) return;
    setLoadingSpecialPlates(true);
    authFetch(API_ENDPOINTS.getSpecialPlates.replace("##siteid##", selectedSite.id))
      .then(res => res.json())
      .then(data => setSpecialPlateRules(data.rules ?? []))
      .catch(() => setErrorMessage(t("Failed to load special plates.")))
      .finally(() => setLoadingSpecialPlates(false));
  }, [activeTab, selectedSite]);

  if (loadingSites) return null;
  if (!selectedSite) return null;

  const categories = selectedSite.available_categories ?? [];

  const patchCategoriesInContext = (updatedCategories) => {
    setSites(sites.map(s =>
      s.id === selectedSite.id
        ? { ...s, available_categories: updatedCategories }
        : s
    ));
  };

  const COUNTRY_LABELS = { AT: `🇦🇹 ${t("Austria")}` };

  // Human-readable label per category, translated — `code` is the stable
  // DB identifier, display text lives entirely here. Falls back to the raw
  // code for any category that hasn't gotten a translation key yet (e.g.
  // a newly added country).
  const PSS_CATEGORY_LABEL_KEYS = {
    police: 'pss_label_police',
    feuerwehr: 'pss_label_firebrigade',
    rettungsdienst: 'pss_label_rettungsdienst',
    taxi: 'pss_label_taxi',
    army: 'pss_label_army',
    post: 'pss_label_post',
  };
  const pssCategoryLabel = (rule) =>
    PSS_CATEGORY_LABEL_KEYS[rule.code] ? t(PSS_CATEGORY_LABEL_KEYS[rule.code]) : rule.code;

  const toggleSpecialPlateRule = (rule) => {
    const enabled = !rule.enabled;
    setSpecialPlateRules(prev => prev.map(r => r.id === rule.id ? { ...r, enabled } : r));
    authFetch(
      API_ENDPOINTS.updateSpecialPlateRule
        .replace("##siteid##", selectedSite.id)
        .replace("##ruleid##", rule.id),
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      }
    ).catch(() => {
      setSpecialPlateRules(prev => prev.map(r => r.id === rule.id ? { ...r, enabled: rule.enabled } : r));
      setErrorMessage(t("Failed to update rule."));
    });
  };

  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditName(cat.name);
    setEditColor(cat.color);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditColor("");
  };

  const saveEdit = async (catId) => {
    try {
      const res = await authFetch(
        `${API_ENDPOINTS.getsites}/${selectedSite.id}/categories/${catId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: editName.trim(), color: editColor }),
        }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || t("Failed to update category"));
      }
      patchCategoriesInContext(
        categories.map(c => c.id === catId ? { ...c, name: editName.trim(), color: editColor } : c)
      );
      cancelEdit();
      setSuccessMessage(t("Category updated."));
    } catch (err) {
      setErrorMessage(err.message);
    }
  };

  const handleCreate = async () => {
    const name = newCatName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await authFetch(
        API_ENDPOINTS.createCategory.replace("##siteid##", selectedSite.id),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name }),
        }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || t("Failed to create category"));
      }
      const created = await res.json();
      patchCategoriesInContext([...categories, { id: created.id, name: created.name, color: created.color, plate_count: 0 }]);
      setNewCatName("");
      setSuccessMessage(t("Category created."));
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setCreating(false);
    }
  };

  const loadGatesForArea = async (areaId) => {
    const url = API_ENDPOINTS.getGates
      .replace("##siteid##", selectedSite.id)
      .replace("##areaid##", areaId);
    try {
      const res = await authFetch(url);
      if (res.ok) {
        const gates = await res.json();
        setAreaGates(prev => ({ ...prev, [areaId]: gates }));
      }
    } catch {}
  };

  const startAreaEdit = (area) => {
    setEditingAreaId(area.id);
    setEditAreaName(area.name);
    setEditAreaSpaces(area.total_spaces != null ? String(area.total_spaces) : "");
    setEditAreaIcon(area.icon || "");
    loadGatesForArea(area.id);
  };

  const cancelAreaEdit = (areaId) => {
    if (areaId !== undefined) {
      const gates = areaGates[areaId] || [];
      setProximityEdits(prev => {
        const n = { ...prev };
        gates.forEach(g => delete n[g.id]);
        return n;
      });
    }
    setEditingAreaId(null);
    setEditAreaName("");
    setEditAreaSpaces("");
    setEditAreaIcon("");
  };

  const patchGateProximity = async (areaId, gateId, edit) => {
    const radius = edit.enabled ? (parseInt(edit.radius, 10) || null) : null;
    const url = API_ENDPOINTS.patchGateProximity
      .replace("##siteid##", selectedSite.id)
      .replace("##areaid##", areaId)
      .replace("##gateid##", gateId);
    const res = await authFetch(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ proximity_radius: radius }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || t("Failed to update gate"));
    }
    const gates = await res.json();
    setAreaGates(prev => ({ ...prev, [areaId]: gates }));
  };

  const saveAreaEdit = async (areaId) => {
    try {
      const totalSpaces = editAreaSpaces !== "" ? parseInt(editAreaSpaces, 10) : null;
      const res = await authFetch(
        `${API_ENDPOINTS.getsites}/${selectedSite.id}/parking-areas/${areaId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: editAreaName.trim(), total_spaces: totalSpaces, icon: editAreaIcon || null }),
        }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || t("Failed to update area"));
      }
      setAreas(areas.map(a => a.id === areaId
        ? { ...a, name: editAreaName.trim(), total_spaces: totalSpaces, icon: editAreaIcon || null }
        : a
      ));
      // save any dirty proximity edits for gates in this area
      const gates = areaGates[areaId] || [];
      const dirtyGates = gates.filter(g => proximityEdits[g.id] !== undefined);
      for (const gate of dirtyGates) {
        await patchGateProximity(areaId, gate.id, proximityEdits[gate.id]);
      }
      setProximityEdits(prev => {
        const n = { ...prev };
        gates.forEach(g => delete n[g.id]);
        return n;
      });
      cancelAreaEdit(areaId);
      setSuccessMessage(t("Area updated."));
    } catch (err) {
      setErrorMessage(err.message);
    }
  };

  const confirmDelete = (cat) => setCategoryToDelete(cat);

  const executeDelete = async () => {
    if (!categoryToDelete) return;
    try {
      const res = await authFetch(
        `${API_ENDPOINTS.getsites}/${selectedSite.id}/categories/${categoryToDelete.id}`,
        { method: "DELETE" }
      );
      if (!res.ok && res.status !== 204) {
        const err = await res.json();
        throw new Error(err.error || t("Failed to delete category"));
      }
      patchCategoriesInContext(categories.filter(c => c.id !== categoryToDelete.id));
      setSuccessMessage(t("Category deleted."));
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setCategoryToDelete(null);
    }
  };

  return (
    <div className="container mt-4">
      <div className="mb-4 d-flex align-items-center gap-3">
        <button className="btn btn-sm btn-light" onClick={() => navigate('/dashboard')}>
          <i className="bi bi-arrow-left"></i>
        </button>
        <h3 className="mb-0">{selectedSite.name}</h3>
        <span className="text-muted">|</span>
        <h5 className="mb-0">{t("Site settings")}</h5>
      </div>

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'areas' ? 'active' : ''}`}
            onClick={() => setActiveTab('areas')}
          >
            <i className="bi bi-p-circle me-2"></i>{t("Areas")}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <i className="bi bi-people me-2"></i>{t("Users")}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            <i className="bi bi-tags me-2"></i>{t("Categories")}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'special-plates' ? 'active' : ''}`}
            onClick={() => setActiveTab('special-plates')}
          >
            <i className="bi bi-shield-check me-2"></i>{t("Special Plates")}
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'export' ? 'active' : ''}`}
            onClick={() => setActiveTab('export')}
          >
            <i className="bi bi-download me-2"></i>{t("export_tab")}
          </button>
        </li>
      </ul>

      {activeTab === 'areas' && (
        <div className="p-4 border ez-rounded-card bg-white shadow-sm">
          {areas.length === 0 ? (
            <p className="text-muted">{t("No areas found.")}</p>
          ) : (
            <div className="row g-2 align-items-start">
              {areas.map(area => (
                <div key={area.id} className="col-12 col-sm-6">
                  <div className="border ez-rounded-tile ez-shadow h-100">
                    {/* Main row */}
                    <div
                      className="d-flex justify-content-between align-items-center p-3"
                      style={{ cursor: 'pointer' }}
                      onClick={() => editingAreaId === area.id ? cancelAreaEdit(area.id) : startAreaEdit(area)}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <i className={`bi ${area.icon || 'bi-p-circle'} text-ez-orange`}></i>
                        <span className="fw-semibold">{area.name}</span>
                        {area.total_spaces != null && (
                          <span className="text-muted small">{area.total_spaces} {t("spaces")}</span>
                        )}
                      </div>
                      <div className="d-flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn ez-button-light btn-sm"
                          onClick={() => editingAreaId === area.id ? cancelAreaEdit(area.id) : startAreaEdit(area)}
                          title={t("edit")}
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                      </div>
                    </div>

                    {/* Inline edit form */}
                    {editingAreaId === area.id && (
                      <div className="p-3 border-top bg-light d-flex flex-column gap-2">
                        <label className="form-label small mb-0">{t("Area name")}</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={editAreaName}
                          onChange={(e) => setEditAreaName(e.target.value)}
                          maxLength={255}

                        />
                        <label className="form-label small mb-0">{t("Available spaces")}</label>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          value={editAreaSpaces}
                          onChange={(e) => setEditAreaSpaces(e.target.value)}
                          min={0}
                          placeholder="—"
                        />
                        <label className="form-label small mb-0">{t("Icon")}</label>
                        <div className="d-flex flex-wrap gap-2">
                          {AREA_ICONS.map(({ icon, label }) => (
                            <button
                              key={icon}
                              title={t(label)}
                              onClick={() => setEditAreaIcon(icon)}
                              className="btn btn-sm"
                              style={{
                                width: 48,
                                height: 48,
                                padding: 0,
                                border: editAreaIcon === icon
                                  ? '2px solid #e8600a'
                                  : '1px solid #ccc',
                                borderRadius: 6,
                                background: editAreaIcon === icon ? '#fff3eb' : 'var(--bs-body-bg)',
                                color: editAreaIcon === icon ? '#e8600a' : 'inherit',
                              }}
                            >
                              <i className={`bi ${icon}`} style={{ fontSize: 22 }}></i>
                            </button>
                          ))}
                        </div>
                        <div className="d-flex gap-2 mt-1">
                          <button className="btn btn-sm ez-button-full" onClick={() => saveAreaEdit(area.id)}>
                            {t("Save")}
                          </button>
                          <button className="btn btn-sm btn-secondary" onClick={() => cancelAreaEdit(area.id)}>
                            {t("Cancel")}
                          </button>
                        </div>

                        {/* Gates sub-section — proximity only */}
                        <hr className="my-3" />
                        <div className="fw-semibold small mb-2">
                          <i className="bi bi-door-closed me-2"></i>{t("Gates")}
                        </div>
                        {(areaGates[area.id] || []).length === 0 ? (
                          <p className="text-muted small">{t("no gates")}</p>
                        ) : (
                          (areaGates[area.id] || []).map(gate => {
                            const edit = proximityEdits[gate.id];
                            const enabled = edit !== undefined ? edit.enabled : gate.proximity_radius != null;
                            const radius = edit !== undefined ? edit.radius : (gate.proximity_radius ?? "");
                            const noCoords = gate.lat == null && gate.lng == null;
                            return (
                              <div key={gate.id} className="border ez-rounded-tile p-2 mb-2 bg-white">
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <span className="fw-semibold small flex-grow-1">{gate.name}</span>
                                  <div className="form-check form-switch mb-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`prox-${gate.id}`}
                                      checked={enabled}
                                      onChange={e => {
                                        const turningOn = e.target.checked;
                                        const currentRadius = String(radius);
                                        setProximityEdits(prev => ({
                                          ...prev,
                                          [gate.id]: {
                                            enabled: turningOn,
                                            radius: turningOn && !currentRadius ? "200" : currentRadius,
                                          }
                                        }));
                                      }}
                                    />
                                    <label className="form-check-label small" htmlFor={`prox-${gate.id}`}>
                                      {t("Proximity")}
                                    </label>
                                  </div>
                                  {enabled && (
                                    <div className="d-flex align-items-center gap-1">
                                      <input
                                        type="number"
                                        min="50"
                                        max="5000"
                                        className="form-control form-control-sm"
                                        style={{ width: 80 }}
                                        placeholder="200"
                                        value={radius}
                                        onChange={e => setProximityEdits(prev => ({
                                          ...prev,
                                          [gate.id]: { enabled: true, radius: e.target.value }
                                        }))}
                                      />
                                      <span className="small text-muted">m</span>
                                    </div>
                                  )}
                                </div>
                                {enabled && noCoords && (
                                  <div className="mt-1 small text-warning">
                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                    {t("No coordinates set")}
                                  </div>
                                )}
                                {enabled && (
                                  <div className="mt-1 small text-muted">
                                    <i className="bi bi-info-circle me-1"></i>
                                    {t("Proximity applies to gate openers only. Site managers can always open.")}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'categories' && (
        <div className="p-4 border ez-rounded-card bg-white shadow-sm">

          {/* Add new category */}
          <div className="d-flex gap-2 mb-4">
            <input
              autoFocus
              type="text"
              className="form-control form-control-sm"
              placeholder={t("New category name")}
              value={newCatName}
              maxLength={50}
              onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <button
              className="btn btn-sm ez-button-full text-nowrap"
              onClick={handleCreate}
              disabled={creating || !newCatName.trim()}
            >
              <i className="bi bi-plus-lg me-1"></i>{t("add")}
            </button>
          </div>

          {categories.length === 0 ? (
            <p className="text-muted">{t("No categories yet.")}</p>
          ) : (
            <div className="row g-2 align-items-start">
              {categories.map(cat => (
                <div key={cat.id} className="col-12 col-sm-6">
                <div className="border ez-rounded-tile ez-shadow h-100">
                  {/* Main row */}
                  <div
                    className="d-flex justify-content-between align-items-center p-3"
                    style={{ cursor: 'pointer' }}
                    onClick={() => editingId === cat.id ? cancelEdit() : startEdit(cat)}
                  >
                    <div className="d-flex align-items-center gap-2 overflow-hidden" style={{ minWidth: 0 }}>
                      <span
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          backgroundColor: cat.color,
                          flexShrink: 0,
                        }}
                      />
                      <span className="fw-semibold text-truncate">{cat.name}</span>
                      {cat.plate_count > 0 && (
                        <span className="text-muted small text-nowrap">{cat.plate_count} {t("plates")}</span>
                      )}
                    </div>
                    <div className="d-flex gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="btn ez-button-light btn-sm"
                        onClick={() => editingId === cat.id ? cancelEdit() : startEdit(cat)}
                        title={t("edit")}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className="btn ez-button-light btn-sm"
                        onClick={() => confirmDelete(cat)}
                        title={t("Delete")}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>

                  {/* Inline edit form */}
                  {editingId === cat.id && (
                    <div className="p-3 border-top bg-light d-flex flex-column gap-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        maxLength={50}
                        autoFocus
                      />
                      <div className="d-flex flex-wrap gap-1">
                        {CATEGORY_COLORS.map(color => (
                          <ColorSwatch
                            key={color}
                            color={color}
                            selected={editColor === color}
                            onClick={() => setEditColor(color)}
                          />
                        ))}
                      </div>
                      <div className="d-flex gap-2 mt-1">
                        <button className="btn btn-sm ez-button-full" onClick={() => saveEdit(cat.id)}>
                          {t("Save")}
                        </button>
                        <button className="btn btn-sm btn-secondary" onClick={cancelEdit}>
                          {t("Cancel")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'special-plates' && (
        <div className="p-4 border ez-rounded-card bg-white shadow-sm">
          <p className="text-muted small mb-1">
            {t("Vehicles matching an enabled category open this site's gates automatically, without needing to be enrolled as a plate.")}
          </p>
          <p className="text-muted small mb-4">
            <i className="bi bi-clock-history me-1"></i>
            {t("Changes can take up to 30 minutes to reach the barrier.")}
          </p>
          {loadingSpecialPlates ? (
            <p className="text-muted">{t("Loading...")}</p>
          ) : specialPlateRules.length === 0 ? (
            <p className="text-muted">{t("No special plate categories available.")}</p>
          ) : (
            Object.entries(
              specialPlateRules.reduce((acc, rule) => {
                (acc[rule.country_code] = acc[rule.country_code] || []).push(rule);
                return acc;
              }, {})
            ).map(([country, rules]) => (
              <div key={country} className="mb-4">
                <h6 className="text-muted text-uppercase small mb-2">
                  {COUNTRY_LABELS[country] ?? country}
                </h6>
                <div className="row g-2 align-items-start">
                  {rules.map(rule => (
                    <div key={rule.id} className="col-12 col-sm-6">
                      <div className="border ez-rounded-tile ez-shadow h-100 p-3 d-flex justify-content-between align-items-center">
                        <span className="fw-semibold">{pssCategoryLabel(rule)}</span>
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`pss-rule-${rule.id}`}
                            checked={rule.enabled}
                            onChange={() => toggleSpecialPlateRule(rule)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'users' && (
        <UserManagement selectedSite={selectedSite} />
      )}

      {activeTab === 'export' && (
        <SiteExport site={selectedSite} areas={areas} />
      )}

      <ConfirmModal
        show={!!categoryToDelete}
        title={t("Delete Category")}
        onCancel={() => setCategoryToDelete(null)}
        onConfirm={executeDelete}
        confirmText={t("Delete")}
        cancelText={t("Cancel")}
      >
        <p>
          {t("Delete category")} <strong>{categoryToDelete?.name}</strong>?
        </p>
        {categoryToDelete?.plate_count > 0 && (
          <p className="text-muted small mb-0">
            <i className="bi bi-info-circle me-1"></i>
            {t("This category is used for")} {categoryToDelete.plate_count} {t("plates")}. {t("Plates will not be deleted, only deassociated.")}
          </p>
        )}
      </ConfirmModal>

      <Notification message={errorMessage} type="danger" onClose={() => setErrorMessage("")} />
      <Notification message={successMessage} type="success" onClose={() => setSuccessMessage("")} />
    </div>
  );
}

export default SiteSettings;
